import { FC, useEffect, useRef } from "react";
import Highcharts, { PlotOptions, YAxisOptions } from "highcharts";
import { Box } from "@mui/material";
import {
  HighchartsChartDataType,
  TooltipMarkupGenerator,
} from "@/storybook/c8r-highcharts/common/utils/types/common";
import { getTooltipOptions } from "@/storybook/c8r-highcharts/common/utils/tooltip/getTooltipOptions";

interface InlineHighchartsProps {
  boxHeight: number;
  pointWidth: number;
  yAxis: YAxisOptions;
  chartHeight?: number;
  plotOptions: PlotOptions;
  chartData: HighchartsChartDataType;
  tooltipMarkupGenerator: TooltipMarkupGenerator;
}

export const InlineHighcharts: FC<InlineHighchartsProps> = ({
  yAxis,
  boxHeight,
  chartData,
  pointWidth,
  plotOptions,
  chartHeight,
  tooltipMarkupGenerator,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);

  const isAllFalse = chartData.series.every((data) =>
    data.data.every((cost) => !cost),
  );

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    // @ts-expect-error  | Something wrong with types, this is waiting for "Options" but it should be an "Element"
    Highcharts.chart(chartRef.current, {
      ...chartDefaultOptions,

      chart: {
        ...chartDefaultOptions.chart,
        height: chartHeight ? `${chartHeight}px` : undefined,
      },

      xAxis: { categories: chartData.categories, visible: true },
      yAxis: {
        ...yAxis,
        min: isAllFalse ? 1 : undefined,
      },

      tooltip: {
        hideDelay: 50,
        showDelay: 50,
        ...getTooltipOptions({
          tooltipMarkupGenerator: tooltipMarkupGenerator,
        }),
        animation: false,
        outside: true,

        positioner: function (labelWidth, labelHeight, point) {
          const chart = this.chart;
          let x = point.plotX + chart.plotLeft - labelWidth / 2;
          x = Math.max(
            chart.plotLeft,
            Math.min(x, chart.plotLeft + chart.plotWidth - labelWidth),
          );

          return { x, y: chart.plotTop - 70 };
        },
      },
      plotOptions: plotOptions,
      series: chartData.series.map((series) => ({
        ...series,
        data: series.data?.map((item) => item ?? smallPointInsteadOfNull),
      })),
    });
  }, [
    chartData,
    isAllFalse,
    pointWidth,
    tooltipMarkupGenerator,
    yAxis,
    chartHeight,
    plotOptions,
  ]);

  return (
    <Box
      ref={chartRef}
      width="100%"
      height={boxHeight}
      sx={{
        "& .highcharts-xaxis .highcharts-axis-line, .highcharts-yaxis .highcharts-axis-line":
          { display: "none" },
      }}
    />
  );
};

const smallPointInsteadOfNull = 0.000001;

const chartDefaultOptions = {
  chart: {
    type: "column",
    backgroundColor: "transparent",
    margin: [0, 0, 0, 0],
    animation: false,
    height: "35px",
  },
  title: { text: undefined },
  legend: { enabled: false },
  credits: { enabled: false },
  exporting: { enabled: false },
  accessibility: { enabled: false },
};
